import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Shield, Award, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import equipmentImage from "@/assets/equipment-trailer.webp";
import equipmentImage400 from "@/assets/equipment-trailer-400.webp";

const AboutPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="section-padding bg-secondary">
      <div className="container-custom mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <img
              src={equipmentImage}
                srcSet={`${equipmentImage400} 400w, ${equipmentImage} 600w`}
                sizes="(max-width: 1024px) 100vw, 400px"
              alt="Lawnworks Of NWA professional landscaping team at work"
              width={1000}
              height={1083}
              loading="lazy"
              className="rounded-lg shadow-lg w-full h-auto"
            />
            {/* Badge */}
            <div className="absolute -bottom-6 -right-6 md:right-8 bg-accent text-accent-foreground p-4 rounded-lg shadow-lg">
              <div className="flex items-center gap-3">
                <Award className="h-8 w-8" />
                <div>
                  <p className="font-heading font-bold text-lg">25+ Years</p>
                  <p className="text-sm">Trusted Locally</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="space-y-6"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              Family-Owned, <span className="text-primary">Locally Trusted</span> for <span className="text-accent">25+ Years</span>
            </h2>

            <p className="text-muted-foreground text-lg leading-relaxed">
              What began as a small, family-focused venture has grown into a trusted and respected landscaping business. The owner made a life-changing decision over 25 years ago to start his own company so he could spend more time close to family.
            </p>

            <p className="text-muted-foreground leading-relaxed">
              Through hard work, dedication, and a commitment to quality service, Lawnworks Of NWA has expanded its services and built a strong reputation for reliability and excellence across Northwest Arkansas.
            </p>

            <div className="flex items-center gap-4 py-4">
              <div className="flex items-center gap-3 bg-card p-4 rounded-lg border border-border">
                <Shield className="h-6 w-6 text-primary" />
                <div>
                  <p className="font-semibold text-foreground">Fully Insured</p>
                  <p className="text-sm text-muted-foreground">Licensed & covered</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-card p-4 rounded-lg border border-border">
                <Award className="h-6 w-6 text-accent" />
                <div>
                  <p className="font-semibold text-foreground">100% Satisfaction</p>
                  <p className="text-sm text-muted-foreground">Guaranteed</p>
                </div>
              </div>
            </div>

            <Link to="/about">
              <Button variant="default" size="lg" className="group">
                Learn More About Us
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutPreview;
